import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Dialog } from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import * as XLSX from "xlsx";
import {
  Users2, Plus, Search, Eye, Edit2, Trash2, X,
  Mail, Phone, Building2, Award, Calendar, User, Copy, Check,
  Upload, ChevronRight, CheckCircle2, FileText, DollarSign, Layers,
  UserCircle, Briefcase, ShieldCheck, MapPin, Download,
} from "lucide-react";
import styles from "../hrms.module.scss";
import { useTheme } from "context/ThemeContext";
import { APIConstant } from "services/api-constant";
import Popup from "components/minor/popup/popup";
import BulkEmployeeUpload from "./BulkEmployeeUpload";

const ax = () => ({
  headers: { Authorization: `Bearer ${sessionStorage.getItem("platform_token") || ""}` }
});

const COLORS = ["#00C8FF","#7C5CFF","#00E5A3","#FFB800","#FF4D6D","#3B82F6"];
const initials = (f="", l="") => `${f[0]||""}${l[0]||""}`.toUpperCase();
const fmt = (n) => `₹${Math.round(n||0).toLocaleString("en-IN")}`;

const Schema = Yup.object({
  first_name: Yup.string().required("Required"),
  last_name:  Yup.string().required("Required"),
  email:      Yup.string().email("Invalid email").required("Required"),
});

const EMPTY = {
  first_name:"", middle_name:"", last_name:"", email:"", phone:"",
  department_id:"", designation_id:"", date_of_joining:"", date_of_birth:"",
  gender:"", marital_status:"", employment_type:"Full Time", branch_location:"",
  address_line1:"", address_line2:"", city:"", state:"", country:"India", pin_code:"",
  emergency_contact:"", status:"Active", manager_id:"",
  pan_number:"", aadhar_number:"", uan_number:"", pf_number:"",
  bank_name:"", bank_account_number:"", bank_ifsc:"", bank_branch:"",
};

// ── Profile helper components ──────────────────────────────────────
function InfoRow({ label, value, mono, T }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
      <div style={{ fontSize:10, fontWeight:700, color:T.dim, textTransform:"uppercase", letterSpacing:"0.07em" }}>{label}</div>
      <div style={{ fontSize:13, color: value ? T.text : T.dim, fontFamily: mono ? "JetBrains Mono,monospace" : "inherit", fontWeight: mono ? 600 : 500 }}>
        {value || "—"}
      </div>
    </div>
  );
}

function EditField({ label, name, type="text", value, onChange, T }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
      <div style={{ fontSize:10, fontWeight:700, color:T.dim, textTransform:"uppercase", letterSpacing:"0.07em" }}>{label}</div>
      <input
        type={type}
        value={value ?? ""}
        onChange={ev => onChange(name, ev.target.value)}
        style={{
          background:T.inp_bg, border:`1px solid ${T.border}`, borderRadius:6,
          color:T.text, fontSize:13, padding:"6px 10px", outline:"none",
          fontFamily:"Outfit,sans-serif", width:"100%", boxSizing:"border-box",
        }}
      />
    </div>
  );
}

function EditSelect({ label, name, value, options, onChange, T }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
      <div style={{ fontSize:10, fontWeight:700, color:T.dim, textTransform:"uppercase", letterSpacing:"0.07em" }}>{label}</div>
      <select
        value={value ?? ""}
        onChange={ev => onChange(name, ev.target.value)}
        style={{
          background:T.inp_bg, border:`1px solid ${T.border}`, borderRadius:6,
          color:T.text, fontSize:13, padding:"6px 10px", outline:"none",
          fontFamily:"Outfit,sans-serif", width:"100%", boxSizing:"border-box",
        }}
      >
        {options.map(o => <option key={o.value} value={o.value} style={{ background:T.opt_bg, color:T.text }}>{o.label}</option>)}
      </select>
    </div>
  );
}

function SecHead({ icon, label, color, T }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:14, paddingBottom:8, borderBottom:`1px solid ${color}22` }}>
      <div style={{ width:28, height:28, borderRadius:7, background:`${color}18`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14 }}>{icon}</div>
      <span style={{ fontSize:11, fontWeight:800, color, textTransform:"uppercase", letterSpacing:"0.08em" }}>{label}</span>
    </div>
  );
}

function ToggleSwitch({ label, value, onChange, T, readOnly }) {
  const on = value === true || value === "Yes" || value === 1;
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 0", borderBottom:`1px solid ${T.toggle_bg}` }}>
      <span style={{ fontSize:13, color:T.muted }}>{label}</span>
      <div
        onClick={() => !readOnly && onChange && onChange(!on)}
        style={{
          width:40, height:22, borderRadius:11, cursor: readOnly ? "default" : "pointer",
          background: on ? T.cyan : T.toggle_bg,
          position:"relative", transition:"background 0.25s",
          border:`1px solid ${on ? T.cyan : T.border}`,
          flexShrink:0,
        }}>
        <div style={{
          width:16, height:16, borderRadius:"50%",
          background: on ? "#fff" : T.dim,
          position:"absolute", top:2,
          left: on ? 20 : 2,
          transition:"left 0.25s",
          boxShadow:"0 1px 3px rgba(0,0,0,0.25)",
        }}/>
      </div>
    </div>
  );
}

const dash = (v) => v || "—";

// ═══════════════════════════════════════════════════════════════════
// FULL PROFILE MODAL — 4 tabs: Personal | Employment | Salary | Statutory
// ═══════════════════════════════════════════════════════════════════
function FullProfileModal({ open, onClose, emp, T, departments, designations, employees: empList, onRefresh }) {
  const [tab, setTab]           = useState("personal");
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving]     = useState(false);
  // ── Reopen cache ────────────────────────────────────────────────
  // Component stays mounted (just hidden) when modal closes, so this ref
  // persists between opens. Fills null fields from backend with last-saved
  // values — handles the period before DB migration is run.
  const lastSavedRef = React.useRef({});

  useEffect(() => {
    if (!open || !emp) return;
    setTab("personal"); setData(null); setLoading(true); setEditMode(false);
    const load = async () => {
      try {
        const [empRes, assignRes, bdRes] = await Promise.allSettled([
          axios.get(`${APIConstant.employees}${emp.id}`, ax()),
          axios.get(APIConstant.salaryAssignments, ax()),
          axios.get(`${APIConstant.salaryStructures}breakdown/${emp.id}`, ax()),
        ]);
        const allAssignments = assignRes.status === "fulfilled" ? (assignRes.value.data || []) : [];
        // ── Merge server data with reopen cache ─────────────────
        // Backend returns null for new fields when DB column not yet migrated.
        // lastSavedRef restores what the user last typed so it shows on reopen.
        const freshFull  = empRes.status === "fulfilled" ? (empRes.value.data || emp) : emp;
        const cached     = lastSavedRef.current[emp.id] || {};
        const mergedFull = { ...freshFull };
        Object.keys(cached).forEach(k => {
          if ((mergedFull[k] === null || mergedFull[k] === undefined) &&
              cached[k] !== null && cached[k] !== undefined && cached[k] !== "") {
            mergedFull[k] = cached[k];
          }
        });
        setData({
          full:      mergedFull,
          salary:    allAssignments.filter(a => a.employee_id === emp.id),
          breakdown: bdRes.status === "fulfilled" ? (bdRes.value.data || null) : null,
        });
      } catch { setData({ full: emp, salary: [], breakdown: null }); }
      finally { setLoading(false); }
    };
    load();
  }, [open, emp]);

  useEffect(() => {
    if (data?.full) setEditData({ ...data.full });
  }, [data]);

  if (!emp) return null;
  const e         = data?.full      || emp;
  const salary    = data?.salary    || [];
  const breakdown = data?.breakdown || null;

  const setField   = (name, val) => setEditData(prev => ({ ...prev, [name]: val }));
  const startEdit  = () => { setEditData({ ...e }); setEditMode(true); };
  const cancelEdit = () => setEditMode(false);

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...editData,
        department_id:  editData.department_id  ? parseInt(editData.department_id)  : null,
        designation_id: editData.designation_id ? parseInt(editData.designation_id) : null,
        manager_id:     editData.manager_id     ? parseInt(editData.manager_id)     : null,
        // type="number" inputs return strings — convert explicitly
        pf_wage: editData.pf_wage !== "" && editData.pf_wage != null ? parseFloat(editData.pf_wage) : null,
        vpf:     editData.vpf     !== "" && editData.vpf     != null ? parseFloat(editData.vpf)     : null,
      };

      // PUT — backend returns _enrich(emp, db) with all saved + computed fields
      const putRes = await axios.put(`${APIConstant.employees}${e.id}`, payload, ax());

      // ── Merge strategy ──────────────────────────────────────────
      // Base  = PUT response → accurate computed fields (department_name,
      //         designation_title, manager_name) that editData doesn't have.
      // Fill  = editData → for any field PUT response returns as null/undefined
      //         (happens when DB column not yet migrated).
      // Ensures UI shows what the user typed regardless of DB migration status.
      const fresh   = putRes.data || {};
      const display = { ...fresh };
      Object.keys(editData).forEach(k => {
        const backendVal = display[k];
        const localVal   = editData[k];
        if (
          (backendVal === null || backendVal === undefined) &&
          localVal !== null && localVal !== undefined && localVal !== ""
        ) {
          display[k] = localVal;
        }
      });

      setData(prev => ({ ...prev, full: display }));
      lastSavedRef.current[e.id] = display;   // ← persist for next reopen
      setEditMode(false);
      if (onRefresh) onRefresh();
    } catch(err) {
      alert(err.response?.data?.detail || "Error saving changes");
    } finally { setSaving(false); }
  };

  const TABS = [
    { key:"personal",   label:"Personal",   emoji:"👤" },
    { key:"employment", label:"Employment",  emoji:"🏢" },
    { key:"salary",     label:"Salary",      emoji:"💰" },
    { key:"statutory",  label:"Statutory",   emoji:"🏛️" },
  ];

  const editBar = (
    <div style={{ display:"flex", justifyContent:"flex-end", gap:8, marginBottom:20 }}>
      {editMode ? (
        <>
          <button onClick={cancelEdit} style={{
            padding:"7px 16px", borderRadius:7, border:`1px solid ${T.border}`,
            background:"none", color:T.muted, cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:12, fontWeight:600,
          }}>Cancel</button>
          <button onClick={saveEdit} disabled={saving} style={{
            padding:"7px 16px", borderRadius:7, border:"none",
            background:T.cyan, color:"#fff", cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:12, fontWeight:700,
            opacity: saving ? 0.6 : 1,
          }}>{saving ? "Saving…" : "Save Changes"}</button>
        </>
      ) : (
        <button onClick={startEdit} style={{
          padding:"7px 14px", borderRadius:7, border:`1px solid ${T.border}`,
          background:"none", color:T.cyan, cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:12, fontWeight:600,
          display:"flex", alignItems:"center", gap:5,
        }}><Edit2 size={12}/> Edit</button>
      )}
    </div>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false}
      PaperProps={{ style: { borderRadius:0, background:"transparent", boxShadow:"none" } }}>
      <div style={{ background:T.card, border:`1px solid ${T.cyan}33`, borderRadius:16,
        width:900, maxHeight:"88vh", display:"flex", flexDirection:"column",
        fontFamily:"Outfit,sans-serif", overflow:"hidden" }}>

        {/* ── Header ── */}
        <div style={{ padding:"20px 28px", background:`linear-gradient(135deg,${T.cyan}14,${T.purple}10)`,
          borderBottom:`1px solid ${T.border}`, display:"flex", alignItems:"center", gap:18, flexShrink:0 }}>
          <div style={{ width:60, height:60, borderRadius:15, flexShrink:0,
            background: e.avatar_color || COLORS[0],
            display:"flex", alignItems:"center", justifyContent:"center",
            fontSize:20, fontWeight:800, color:"#fff",
            boxShadow:`0 0 0 3px ${T.card}, 0 0 0 5px ${T.cyan}40` }}>
            {initials(e.first_name, e.last_name)}
          </div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:20, fontWeight:800, color:T.text, marginBottom:4 }}>
              {e.first_name}{e.middle_name ? ` ${e.middle_name}` : ""} {e.last_name}
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
              <span style={{ fontSize:13, color:T.cyan, fontWeight:600 }}>{e.designation_title || "—"}</span>
              {e.department_name && <span style={{ fontSize:12, color:T.muted }}>· {e.department_name}</span>}
              {e.employee_code && (
                <span style={{ fontSize:11, fontFamily:"JetBrains Mono,monospace",
                  padding:"2px 8px", borderRadius:6, background:`${T.cyan}18`, color:T.cyan }}>
                  {e.employee_code}
                </span>
              )}
              <span style={{ fontSize:11, fontWeight:700, padding:"2px 10px", borderRadius:20,
                background: e.status==="Active"?`${T.green}18`:e.status==="On Leave"?`${T.amber}18`:`${T.red}18`,
                color: e.status==="Active"?T.green:e.status==="On Leave"?T.amber:T.red }}>
                {e.status || "Active"}
              </span>
            </div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:T.muted, padding:6 }}>
            <X size={18}/>
          </button>
        </div>

        {/* ── Tab bar ── */}
        <div style={{ display:"flex", borderBottom:`1px solid ${T.border}`, background:T.card2||T.card, flexShrink:0 }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => { setTab(t.key); setEditMode(false); }} style={{
              display:"flex", alignItems:"center", gap:7, padding:"13px 24px",
              border:"none", borderBottom:`2px solid ${tab===t.key?T.cyan:"transparent"}`,
              background:"transparent", color: tab===t.key ? T.cyan : T.muted,
              cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:13,
              fontWeight: tab===t.key ? 700 : 500, transition:"all 0.15s",
            }}>
              <span>{t.emoji}</span> {t.label}
            </button>
          ))}
        </div>

        {/* ── Body ── */}
        <div style={{ overflowY:"auto", flex:1 }}>
          {loading && (
            <div style={{ padding:60, textAlign:"center", color:T.muted, fontSize:13 }}>
              <div style={{ width:28, height:28, border:`3px solid ${T.cyan}`, borderTopColor:"transparent",
                borderRadius:"50%", animation:"spin 0.8s linear infinite", margin:"0 auto 14px" }}/>
              Loading employee details…
            </div>
          )}

          {/* ─── PERSONAL TAB ─── */}
          {!loading && tab === "personal" && (
            <div style={{ padding:28 }}>
              {editBar}
              <SecHead icon="👤" label="Basic Information" color={T.cyan} T={T}/>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:18, marginBottom:28 }}>
                {editMode ? (
                  <>
                    <EditField label="First Name"       name="first_name"       value={editData.first_name}      onChange={setField} T={T}/>
                    <EditField label="Middle Name"      name="middle_name"      value={editData.middle_name}     onChange={setField} T={T}/>
                    <EditField label="Last Name"        name="last_name"        value={editData.last_name}       onChange={setField} T={T}/>
                    <EditField label="Email ID"         name="email"            type="email" value={editData.email} onChange={setField} T={T}/>
                    <EditField label="Mobile"           name="phone"            value={editData.phone}           onChange={setField} T={T}/>
                    <EditSelect label="Gender" name="gender" value={editData.gender} onChange={setField} T={T}
                      options={[{value:"",label:"Select"},{value:"Male",label:"Male"},{value:"Female",label:"Female"},{value:"Other",label:"Other"}]}/>
                    <EditField label="Date of Birth"    name="date_of_birth"    type="date" value={editData.date_of_birth} onChange={setField} T={T}/>
                    <EditSelect label="Marital Status" name="marital_status" value={editData.marital_status} onChange={setField} T={T}
                      options={[{value:"",label:"Select"},{value:"Single",label:"Single"},{value:"Married",label:"Married"},{value:"Divorced",label:"Divorced"},{value:"Widowed",label:"Widowed"}]}/>
                    <EditField label="Disability Level" name="disability_level" value={editData.disability_level} onChange={setField} T={T}/>
                  </>
                ) : (
                  <>
                    <InfoRow label="First Name"       value={e.first_name}                          T={T}/>
                    <InfoRow label="Middle Name"      value={e.middle_name}                         T={T}/>
                    <InfoRow label="Last Name"        value={e.last_name}                           T={T}/>
                    <InfoRow label="Email ID"         value={e.email}                               T={T}/>
                    <InfoRow label="Mobile"           value={e.phone}                               T={T}/>
                    <InfoRow label="Gender"           value={e.gender}                              T={T}/>
                    <InfoRow label="Date of Birth"    value={e.date_of_birth}                       T={T}/>
                    <InfoRow label="Marital Status"   value={e.marital_status}                      T={T}/>
                    <InfoRow label="Disability Level" value={e.disability_level || "No Disability"} T={T}/>
                  </>
                )}
              </div>

              <SecHead icon="🪪" label="Identity & Tax" color={T.amber} T={T}/>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:18, marginBottom:28 }}>
                {editMode ? (
                  <>
                    <EditField label="PAN Number"    name="pan_number"    value={editData.pan_number}    onChange={setField} T={T}/>
                    <EditField label="Aadhar Number" name="aadhar_number" value={editData.aadhar_number} onChange={setField} T={T}/>
                    <EditField label="UAN Number"    name="uan_number"    value={editData.uan_number}    onChange={setField} T={T}/>
                  </>
                ) : (
                  <>
                    <InfoRow label="PAN Number"    value={e.pan_number}    mono T={T}/>
                    <InfoRow label="Aadhar Number" value={e.aadhar_number} mono T={T}/>
                    <InfoRow label="UAN Number"    value={e.uan_number}    mono T={T}/>
                  </>
                )}
              </div>

              <SecHead icon="👨‍👩‍👧" label="Family Information" color={T.purple} T={T}/>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:18, marginBottom:28 }}>
                {editMode ? (
                  <>
                    <EditField label="Father's Name"     name="father_name"       value={editData.father_name}       onChange={setField} T={T}/>
                    <EditField label="Mother's Name"     name="mother_name"       value={editData.mother_name}       onChange={setField} T={T}/>
                    <EditField label="Spouse's Name"     name="spouse_name"       value={editData.spouse_name}       onChange={setField} T={T}/>
                    <EditField label="Emergency Contact" name="emergency_contact" value={editData.emergency_contact} onChange={setField} T={T}/>
                  </>
                ) : (
                  <>
                    <InfoRow label="Father's Name"     value={e.father_name}       T={T}/>
                    <InfoRow label="Mother's Name"     value={e.mother_name}       T={T}/>
                    <InfoRow label="Spouse's Name"     value={e.spouse_name}       T={T}/>
                    <InfoRow label="Emergency Contact" value={e.emergency_contact} T={T}/>
                  </>
                )}
              </div>

              <SecHead icon="📍" label="Address Information" color={T.green} T={T}/>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:18, marginBottom:28 }}>
                {editMode ? (
                  <>
                    <EditField label="Address Line 1"   name="address_line1" value={editData.address_line1 || editData.address} onChange={setField} T={T}/>
                    <EditField label="Address Line 2"   name="address_line2" value={editData.address_line2} onChange={setField} T={T}/>
                    <EditField label="Country"          name="country"       value={editData.country}       onChange={setField} T={T}/>
                    <EditField label="State / Province" name="state"         value={editData.state}         onChange={setField} T={T}/>
                    <EditField label="City"             name="city"          value={editData.city}          onChange={setField} T={T}/>
                    <EditField label="Pin Code"         name="pin_code"      value={editData.pin_code}      onChange={setField} T={T}/>
                  </>
                ) : (
                  <>
                    <InfoRow label="Address Line 1"   value={e.address_line1 || e.address} T={T}/>
                    <InfoRow label="Address Line 2"   value={e.address_line2}              T={T}/>
                    <InfoRow label="Country"          value={e.country || "India"}         T={T}/>
                    <InfoRow label="State / Province" value={e.state}                      T={T}/>
                    <InfoRow label="City"             value={e.city}                       T={T}/>
                    <InfoRow label="Pin Code"         value={e.pin_code}              mono T={T}/>
                  </>
                )}
              </div>

              <SecHead icon="🏦" label="Bank Account Details" color={T.green} T={T}/>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:18 }}>
                {editMode ? (
                  <>
                    <EditField label="Bank Name"      name="bank_name"           value={editData.bank_name}           onChange={setField} T={T}/>
                    <EditField label="Account Number" name="bank_account_number" value={editData.bank_account_number} onChange={setField} T={T}/>
                    <EditField label="IFSC Code"      name="bank_ifsc"           value={editData.bank_ifsc}           onChange={setField} T={T}/>
                    <EditField label="Branch"         name="bank_branch"         value={editData.bank_branch}         onChange={setField} T={T}/>
                  </>
                ) : (
                  <>
                    <InfoRow label="Bank Name"      value={e.bank_name}           T={T}/>
                    <InfoRow label="Account Number" value={e.bank_account_number} mono T={T}/>
                    <InfoRow label="IFSC Code"      value={e.bank_ifsc}           mono T={T}/>
                    <InfoRow label="Branch"         value={e.bank_branch}         T={T}/>
                  </>
                )}
              </div>
            </div>
          )}

          {/* ─── EMPLOYMENT TAB ─── */}
          {!loading && tab === "employment" && (
            <div style={{ padding:28 }}>
              {editBar}
              <SecHead icon="🏢" label="General Employment Details" color={T.cyan} T={T}/>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:18, marginBottom:28 }}>
                {editMode ? (
                  <>
                    <EditField label="Employee Code"     name="employee_code"   value={editData.employee_code}  onChange={setField} T={T}/>
                    <EditField label="Joining Date"      name="date_of_joining" type="date" value={editData.date_of_joining} onChange={setField} T={T}/>
                    <EditSelect label="Employment Type" name="employment_type" value={editData.employment_type} onChange={setField} T={T}
                      options={[{value:"",label:"Select"},{value:"Full Time",label:"Full Time"},{value:"Part Time",label:"Part Time"},{value:"Contract",label:"Contract"},{value:"Intern",label:"Intern"}]}/>
                    <EditField label="Skill Level"      name="skill_level"     value={editData.skill_level}    onChange={setField} T={T}/>
                    <EditSelect label="Department" name="department_id" value={String(editData.department_id||"")} onChange={setField} T={T}
                      options={[{value:"",label:"Select Department"}, ...(departments||[]).map(d=>({value:String(d.id),label:d.name}))]}/>
                    <EditSelect label="Designation" name="designation_id" value={String(editData.designation_id||"")} onChange={setField} T={T}
                      options={[{value:"",label:"Select Designation"}, ...(designations||[]).map(d=>({value:String(d.id),label:d.title}))]}/>
                    <EditSelect label="Status" name="status" value={editData.status||"Active"} onChange={setField} T={T}
                      options={[{value:"Active",label:"Active"},{value:"On Leave",label:"On Leave"},{value:"Inactive",label:"Inactive"}]}/>
                    <EditSelect label="Reports To" name="manager_id" value={String(editData.manager_id||"")} onChange={setField} T={T}
                      options={[{value:"",label:"— No manager —"}, ...(empList||[]).filter(em=>em.id!==e.id).map(em=>({value:String(em.id),label:`${em.first_name} ${em.last_name}${em.designation_title?" — "+em.designation_title:""}`}))]}/>
                    <EditField label="Branch / Location" name="branch_location" value={editData.branch_location} onChange={setField} T={T}/>
                  </>
                ) : (
                  <>
                    <InfoRow label="Employee Code"     value={e.employee_code}                     mono T={T}/>
                    <InfoRow label="Joining Date"      value={e.date_of_joining}                        T={T}/>
                    <InfoRow label="Employment Type"   value={e.employment_type || "Full Time"}         T={T}/>
                    <InfoRow label="Skill Level"       value={e.skill_level}                            T={T}/>
                    <InfoRow label="Reports To"        value={e.manager_name || e.reporting_supervisor} T={T}/>
                    <InfoRow label="Department"        value={e.department_name}                        T={T}/>
                    <InfoRow label="Designation"       value={e.designation_title}                      T={T}/>
                    <InfoRow label="Designation Level" value={e.designation_level}                      T={T}/>
                    <InfoRow label="Branch / Location" value={e.branch_location}                        T={T}/>
                  </>
                )}
              </div>

              <SecHead icon="📋" label="Employment Status History" color={T.purple} T={T}/>
              <div style={{ border:`1px solid ${T.border}`, borderRadius:10, overflow:"hidden", marginBottom:28 }}>
                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                  <thead>
                    <tr style={{ background:T.card2||T.toggle_bg }}>
                      {["Effective From","Effective To","Conf. Due Date","Conf. Date","Status"].map(h=>(
                        <th key={h} style={{ padding:"9px 14px", textAlign:"left", fontSize:10.5, color:T.dim,
                          fontWeight:700, textTransform:"uppercase", letterSpacing:"0.07em", borderBottom:`1px solid ${T.border}` }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(e.status_history && e.status_history.length > 0)
                      ? e.status_history.map((row, i) => (
                          <tr key={i} style={{ borderBottom:`1px solid ${T.toggle_bg}` }}>
                            <td style={{ padding:"9px 14px", color:T.text }}>{dash(row.effective_from)}</td>
                            <td style={{ padding:"9px 14px", color:T.muted }}>{dash(row.effective_to)}</td>
                            <td style={{ padding:"9px 14px", color:T.muted }}>{dash(row.confirmation_due_date)}</td>
                            <td style={{ padding:"9px 14px", color:T.muted }}>{row.confirmation_date || "NA"}</td>
                            <td style={{ padding:"9px 14px" }}>
                              <span style={{ fontSize:11, fontWeight:700, padding:"2px 10px", borderRadius:20, background:`${T.cyan}18`, color:T.cyan }}>
                                {dash(row.status)}
                              </span>
                            </td>
                          </tr>
                        ))
                      : (
                          <tr>
                            <td style={{ padding:"9px 14px", color:T.text }}>{dash(e.date_of_joining)}</td>
                            <td style={{ padding:"9px 14px", color:T.muted }}>—</td>
                            <td style={{ padding:"9px 14px", color:T.muted }}>—</td>
                            <td style={{ padding:"9px 14px", color:T.muted }}>NA</td>
                            <td style={{ padding:"9px 14px" }}>
                              <span style={{ fontSize:11, fontWeight:700, padding:"2px 10px", borderRadius:20, background:`${T.green}18`, color:T.green }}>Active</span>
                            </td>
                          </tr>
                        )
                    }
                  </tbody>
                </table>
              </div>

              <SecHead icon="📌" label="Job Information" color={T.amber} T={T}/>
              <div style={{ border:`1px solid ${T.border}`, borderRadius:10, overflow:"hidden" }}>
                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                  <thead>
                    <tr style={{ background:T.card2||T.toggle_bg }}>
                      {["Effective From","Effective To","Branch / Location","Department","Designation","Reporting To"].map(h=>(
                        <th key={h} style={{ padding:"9px 14px", textAlign:"left", fontSize:10.5, color:T.dim,
                          fontWeight:700, textTransform:"uppercase", letterSpacing:"0.07em", borderBottom:`1px solid ${T.border}` }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(e.job_history && e.job_history.length > 0)
                      ? e.job_history.map((row, i) => (
                          <tr key={i} style={{ borderBottom:`1px solid ${T.toggle_bg}` }}>
                            <td style={{ padding:"9px 14px", color:T.text }}>{dash(row.effective_from)}</td>
                            <td style={{ padding:"9px 14px", color:T.muted }}>{dash(row.effective_to)}</td>
                            <td style={{ padding:"9px 14px", color:T.muted }}>{dash(row.branch_location)}</td>
                            <td style={{ padding:"9px 14px", color:T.muted }}>{dash(row.department)}</td>
                            <td style={{ padding:"9px 14px", color:T.text, fontWeight:500 }}>{dash(row.designation)}</td>
                            <td style={{ padding:"9px 14px", color:T.muted }}>{dash(row.reporting_supervisor)}</td>
                          </tr>
                        ))
                      : (
                          <tr>
                            <td style={{ padding:"9px 14px", color:T.text }}>{dash(e.date_of_joining)}</td>
                            <td style={{ padding:"9px 14px", color:T.muted }}>—</td>
                            <td style={{ padding:"9px 14px", color:T.muted }}>{dash(e.branch_location)}</td>
                            <td style={{ padding:"9px 14px", color:T.muted }}>{dash(e.department_name)}</td>
                            <td style={{ padding:"9px 14px", color:T.text, fontWeight:500 }}>{dash(e.designation_title)}</td>
                            <td style={{ padding:"9px 14px", color:T.muted }}>{dash(e.manager_name)}</td>
                          </tr>
                        )
                    }
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ─── SALARY TAB ─── */}
          {!loading && tab === "salary" && (
            <div style={{ padding:28 }}>
              {!breakdown && salary.length === 0 && (
                <div style={{ textAlign:"center", padding:"48px 0", color:T.dim }}>
                  <div style={{ fontSize:40, marginBottom:12 }}>💰</div>
                  <div style={{ fontSize:14, fontWeight:600, color:T.muted, marginBottom:6 }}>No salary structure assigned</div>
                  <div style={{ fontSize:12, color:T.dim }}>Go to <strong style={{ color:T.text }}>Payroll → Salary Structures</strong> to assign one.</div>
                </div>
              )}
              {breakdown && (
                <>
                  <SecHead icon="📐" label="Current Salary Breakdown" color={T.purple} T={T}/>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:20 }}>
                    {[
                      { label:"Annual CTC",    val: fmt(breakdown.cost_to_company || breakdown.monthly_ctc * 12), color:T.cyan  },
                      { label:"Monthly Gross", val: fmt(breakdown.gross),                                          color:T.amber },
                      { label:"Net Salary",    val: fmt(breakdown.net_salary),                                     color:T.green },
                      { label:"TDS / Month",   val: breakdown.tds_monthly > 0 ? fmt(breakdown.tds_monthly) : "Nil", color:T.red },
                    ].map(({ label, val, color }) => (
                      <div key={label} style={{ background:T.toggle_bg, borderRadius:10, padding:"14px", textAlign:"center" }}>
                        <div style={{ fontSize:9.5, color:T.dim, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:6 }}>{label}</div>
                        <div style={{ fontSize:16, fontWeight:800, color, fontFamily:"JetBrains Mono,monospace" }}>{val}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16,
                    background:`${T.purple}0A`, border:`1px solid ${T.purple}20`, borderRadius:10, padding:"12px 16px" }}>
                    <div>
                      <div style={{ fontSize:13, fontWeight:700, color:T.text }}>{breakdown.structure_name || "Salary Structure"}</div>
                      <div style={{ fontSize:11.5, color:T.muted, marginTop:2 }}>
                        Effective from: {breakdown.effective_from || dash(e.date_of_joining)}
                        {breakdown.chosen_regime && <> · Tax Regime: <span style={{ color:T.amber, fontWeight:600 }}>{breakdown.chosen_regime}</span></>}
                      </div>
                    </div>
                    <div style={{ textAlign:"right" }}>
                      <div style={{ fontSize:10, color:T.dim, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:3 }}>Monthly CTC</div>
                      <div style={{ fontSize:18, fontWeight:800, color:T.cyan, fontFamily:"JetBrains Mono,monospace" }}>{fmt(breakdown.monthly_ctc)}</div>
                    </div>
                  </div>
                  <div style={{ border:`1px solid ${T.border}`, borderRadius:10, overflow:"hidden", marginBottom:24 }}>
                    <div style={{ padding:"8px 16px", background:`${T.amber}0A`, borderBottom:`1px solid ${T.border}` }}>
                      <span style={{ fontSize:10.5, fontWeight:700, color:T.amber, textTransform:"uppercase", letterSpacing:"0.07em" }}>Earnings</span>
                    </div>
                    {Object.entries(breakdown.earnings || {}).map(([k, v]) => (
                      <div key={k} style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
                        padding:"9px 16px", borderBottom:`1px solid ${T.toggle_bg}` }}>
                        <span style={{ fontSize:13, color:T.muted }}>{k}</span>
                        <span style={{ fontSize:13, fontFamily:"JetBrains Mono,monospace", fontWeight:600, color:T.text }}>{fmt(v)}</span>
                      </div>
                    ))}
                    <div style={{ display:"flex", justifyContent:"space-between", padding:"10px 16px",
                      background:`${T.amber}0C`, borderBottom:`1px solid ${T.border}` }}>
                      <span style={{ fontSize:13, fontWeight:700, color:T.amber }}>Gross</span>
                      <span style={{ fontSize:14, fontWeight:800, fontFamily:"JetBrains Mono,monospace", color:T.amber }}>{fmt(breakdown.gross)}</span>
                    </div>
                    {Object.entries(breakdown.deductions || {})
                      .filter(([k]) => !k.toLowerCase().includes("employer") && !k.toLowerCase().startsWith("er ") && k !== "TDS")
                      .map(([k, v]) => (
                        <div key={k} style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
                          padding:"9px 16px", borderBottom:`1px solid ${T.toggle_bg}` }}>
                          <span style={{ fontSize:13, color:T.muted }}>− {k}</span>
                          <span style={{ fontSize:13, fontFamily:"JetBrains Mono,monospace", fontWeight:600, color:T.red }}>{fmt(v)}</span>
                        </div>
                      ))}
                    {breakdown.take_home_before_tax > 0 && (
                      <div style={{ display:"flex", justifyContent:"space-between", padding:"9px 16px",
                        background:`${T.cyan}08`, borderBottom:`1px solid ${T.border}` }}>
                        <span style={{ fontSize:13, fontWeight:600, color:T.cyan }}>Take Home (before TDS)</span>
                        <span style={{ fontSize:13, fontFamily:"JetBrains Mono,monospace", fontWeight:700, color:T.cyan }}>{fmt(breakdown.take_home_before_tax)}</span>
                      </div>
                    )}
                    {breakdown.tds_monthly > 0 && (
                      <div style={{ display:"flex", justifyContent:"space-between", padding:"9px 16px", borderBottom:`1px solid ${T.toggle_bg}` }}>
                        <span style={{ fontSize:13, color:T.muted }}>− TDS ({breakdown.chosen_regime || ""})</span>
                        <span style={{ fontSize:13, fontFamily:"JetBrains Mono,monospace", fontWeight:600, color:T.red }}>{fmt(breakdown.tds_monthly)}</span>
                      </div>
                    )}
                    <div style={{ display:"flex", justifyContent:"space-between", padding:"12px 16px", background:`${T.green}0C` }}>
                      <span style={{ fontSize:14, fontWeight:800, color:T.green }}>Net Salary</span>
                      <span style={{ fontSize:15, fontWeight:900, fontFamily:"JetBrains Mono,monospace", color:T.green }}>{fmt(breakdown.net_salary)}</span>
                    </div>
                    {Object.entries(breakdown.deductions || {})
                      .filter(([k]) => k.toLowerCase().includes("employer") || k.toLowerCase().startsWith("er "))
                      .length > 0 && (
                        <>
                          <div style={{ padding:"8px 16px", background:`${T.purple}0A`, borderTop:`1px solid ${T.border}`, borderBottom:`1px solid ${T.toggle_bg}` }}>
                            <span style={{ fontSize:10.5, fontWeight:700, color:T.purple, textTransform:"uppercase", letterSpacing:"0.07em" }}>Employer Contributions</span>
                          </div>
                          {Object.entries(breakdown.deductions)
                            .filter(([k]) => k.toLowerCase().includes("employer") || k.toLowerCase().startsWith("er "))
                            .map(([k, v]) => (
                              <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"9px 16px", borderBottom:`1px solid ${T.toggle_bg}` }}>
                                <span style={{ fontSize:13, color:T.muted }}>{k}</span>
                                <span style={{ fontSize:13, fontFamily:"JetBrains Mono,monospace", color:T.purple }}>{fmt(v)}</span>
                              </div>
                            ))}
                          <div style={{ display:"flex", justifyContent:"space-between", padding:"10px 16px", background:`${T.cyan}08` }}>
                            <span style={{ fontSize:13, fontWeight:700, color:T.cyan }}>CTC (Total)</span>
                            <span style={{ fontSize:14, fontWeight:800, fontFamily:"JetBrains Mono,monospace", color:T.cyan }}>{fmt(breakdown.cost_to_company)}</span>
                          </div>
                        </>
                      )}
                  </div>
                </>
              )}
              {salary.length > 0 && (
                <>
                  <SecHead icon="📋" label="CTC History" color={T.green} T={T}/>
                  <div style={{ border:`1px solid ${T.border}`, borderRadius:12, overflow:"hidden" }}>
                    <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                      <thead>
                        <tr style={{ background:T.card2||T.toggle_bg }}>
                          {["#","Structure","Effective From","Annual CTC","Monthly CTC",""].map(h=>(
                            <th key={h} style={{ padding:"10px 14px", textAlign:"left", fontSize:10.5, color:T.dim,
                              fontWeight:700, textTransform:"uppercase", letterSpacing:"0.07em", borderBottom:`1px solid ${T.border}` }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {salary.map((row, i) => {
                          const isCurrent = i === salary.length - 1;
                          return (
                            <tr key={row.id || i} style={{ borderBottom:`1px solid ${T.toggle_bg}`, background: isCurrent ? `${T.green}08` : "transparent" }}>
                              <td style={{ padding:"10px 14px", color:T.dim, fontFamily:"JetBrains Mono,monospace", fontSize:12 }}>{i+1}</td>
                              <td style={{ padding:"10px 14px" }}>
                                <span style={{ fontSize:12, padding:"2px 8px", borderRadius:12, background:`${T.cyan}18`, color:T.cyan }}>{row.structure_name || "—"}</span>
                              </td>
                              <td style={{ padding:"10px 14px", color:T.text }}>{dash(row.effective_from)}</td>
                              <td style={{ padding:"10px 14px", fontFamily:"JetBrains Mono,monospace", fontWeight:700, color:T.amber }}>{fmt(row.ctc)}</td>
                              <td style={{ padding:"10px 14px", fontFamily:"JetBrains Mono,monospace", fontWeight:700, color:T.cyan }}>{fmt(row.monthly_ctc)}</td>
                              <td style={{ padding:"10px 14px" }}>
                                {isCurrent
                                  ? <span style={{ fontSize:11, fontWeight:700, padding:"2px 8px", borderRadius:20, background:`${T.green}18`, color:T.green }}>Current</span>
                                  : <span style={{ fontSize:11, fontWeight:700, padding:"2px 8px", borderRadius:20, background:T.toggle_bg, color:T.dim }}>Past</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ─── STATUTORY TAB ─── */}
          {!loading && tab === "statutory" && (
            <div style={{ padding:28 }}>
              {editBar}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:28 }}>
                <div>
                  <SecHead icon="🏛️" label="Provident Fund (PF)" color={T.cyan} T={T}/>
                  <ToggleSwitch label="PF Applicable"                  value={editMode ? editData.pf_applicable                  : e.pf_applicable}                  onChange={on => setField("pf_applicable", on)}                  readOnly={!editMode} T={T}/>
                  <ToggleSwitch label="PF Pension Applicable"          value={editMode ? editData.pf_pension_applicable          : e.pf_pension_applicable}          onChange={on => setField("pf_pension_applicable", on)}          readOnly={!editMode} T={T}/>
                  <ToggleSwitch label="Employee PF Ceiling Applicable" value={editMode ? editData.pf_ceiling_employee_applicable : e.pf_ceiling_employee_applicable} onChange={on => setField("pf_ceiling_employee_applicable", on)} readOnly={!editMode} T={T}/>
                  <ToggleSwitch label="Employer PF Ceiling Applicable" value={editMode ? editData.pf_ceiling_employer_applicable : e.pf_ceiling_employer_applicable} onChange={on => setField("pf_ceiling_employer_applicable", on)} readOnly={!editMode} T={T}/>
                  <div style={{ marginTop:16, display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
                    {editMode ? (
                      <>
                        <EditField label="PF Joining Date" name="pf_joining_date" type="date"   value={editData.pf_joining_date} onChange={setField} T={T}/>
                        <EditField label="PF Number"       name="pf_number"                      value={editData.pf_number}       onChange={setField} T={T}/>
                        <EditField label="UAN Number"      name="uan_number"                      value={editData.uan_number}      onChange={setField} T={T}/>
                        <EditField label="PAN Number"      name="pan_number"                      value={editData.pan_number}      onChange={setField} T={T}/>
                        <EditField label="PF Wage"         name="pf_wage"         type="number" value={editData.pf_wage}          onChange={setField} T={T}/>
                        <EditField label="VPF"             name="vpf"             type="number" value={editData.vpf}              onChange={setField} T={T}/>
                      </>
                    ) : (
                      <>
                        <InfoRow label="PF Joining Date" value={e.pf_joining_date}                    T={T}/>
                        <InfoRow label="PF Number"       value={e.pf_number}        mono              T={T}/>
                        <InfoRow label="UAN Number"      value={e.uan_number}        mono              T={T}/>
                        <InfoRow label="PAN Number"      value={e.pan_number}        mono              T={T}/>
                        <InfoRow label="PF Wage"         value={e.pf_wage  ? fmt(e.pf_wage)  : null}  T={T}/>
                        <InfoRow label="VPF"             value={e.vpf      ? fmt(e.vpf)      : null}   T={T}/>
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <SecHead icon="📑" label="Other Statutory Settings" color={T.purple} T={T}/>
                  <ToggleSwitch label="ESIC Applicable"                 value={editMode ? editData.esic_applicable     : e.esic_applicable}     onChange={on => setField("esic_applicable", on)}     readOnly={!editMode} T={T}/>
                  <ToggleSwitch label="PT Applicable"                   value={editMode ? editData.pt_applicable       : e.pt_applicable}       onChange={on => setField("pt_applicable", on)}       readOnly={!editMode} T={T}/>
                  <ToggleSwitch label="LWF Applicable"                  value={editMode ? editData.lwf_applicable      : e.lwf_applicable}      onChange={on => setField("lwf_applicable", on)}      readOnly={!editMode} T={T}/>
                  <ToggleSwitch label="IT (Income Tax) Applicable"      value={editMode ? editData.it_applicable       : e.it_applicable}       onChange={on => setField("it_applicable", on)}       readOnly={!editMode} T={T}/>
                  <ToggleSwitch label="Gratuity Applicable"             value={editMode ? editData.gratuity_applicable : e.gratuity_applicable} onChange={on => setField("gratuity_applicable", on)} readOnly={!editMode} T={T}/>
                  <ToggleSwitch label="NPS Applicable"                  value={editMode ? editData.nps_applicable      : e.nps_applicable}      onChange={on => setField("nps_applicable", on)}      readOnly={!editMode} T={T}/>
                  <ToggleSwitch label="Decimal Rates Allowed"           value={editMode ? editData.decimal_rates       : e.decimal_rates}       onChange={on => setField("decimal_rates", on)}       readOnly={!editMode} T={T}/>
                  <ToggleSwitch label="Tax on No PAN / Inoperative PAN" value={editMode ? editData.tax_on_no_pan       : e.tax_on_no_pan}       onChange={on => setField("tax_on_no_pan", on)}       readOnly={!editMode} T={T}/>
                </div>
              </div>
              <div style={{ marginTop:28 }}>
                <SecHead icon="📊" label="Tax Regime" color={T.amber} T={T}/>
                <div style={{ display:"flex", gap:14, marginBottom:18 }}>
                  {["New Regime","Old Regime"].map(regime => {
                    const active = ((editMode ? editData.tax_regime : e.tax_regime) || "New Regime") === regime;
                    return (
                      <div key={regime}
                        onClick={() => editMode && setField("tax_regime", regime)}
                        style={{ flex:1, padding:"16px 20px", borderRadius:12, textAlign:"center",
                          cursor: editMode ? "pointer" : "default",
                          background: active ? `${T.amber}18` : T.toggle_bg,
                          border:`2px solid ${active ? T.amber : T.toggle_bg}`,
                          transition:"all 0.2s",
                        }}>
                        <div style={{ fontSize:24, marginBottom:8 }}>{regime === "New Regime" ? "🆕" : "📜"}</div>
                        <div style={{ fontSize:14, fontWeight:800, color: active ? T.amber : T.dim }}>{regime}</div>
                        {active && <div style={{ fontSize:11, color:T.amber, marginTop:4, fontWeight:600 }}>✓ Active</div>}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:16 }}>
                  <InfoRow label="Tax Regime Updated At" value={e.tax_regime_updated_at} T={T}/>
                  <InfoRow label="Tax Regime Updated By" value={e.tax_regime_updated_by} T={T}/>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div style={{ padding:"14px 28px", borderTop:`1px solid ${T.border}`,
          display:"flex", justifyContent:"space-between", alignItems:"center",
          background:T.card2||T.card, flexShrink:0 }}>
          <div style={{ fontSize:12, color:T.dim }}>
            ID: <span style={{ fontFamily:"JetBrains Mono,monospace", color:T.muted }}>{e.id}</span>
            {e.employee_code && <> · Code: <span style={{ fontFamily:"JetBrains Mono,monospace", color:T.cyan }}>{e.employee_code}</span></>}
          </div>
          <button onClick={onClose} style={{ padding:"8px 20px", borderRadius:8, border:`1px solid ${T.border}`,
            background:"none", color:T.muted, cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:600 }}>
            Close
          </button>
        </div>
      </div>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </Dialog>
  );
}


// ── Pagination bar ───────────────────────────────────────────────
// Numbered pagination with ellipsis for large page counts (e.g. 600
// employees @ 20/page = 30 pages). Works off whatever list is passed
// in (already filtered/searched) — caller controls the slicing.
function PaginationBar({ page, totalPages, onChange, totalItems, pageSize, T }) {
  if (totalPages <= 1) return null;

  const delta = 2;
  const pages = [];
  for (let p = 1; p <= totalPages; p++) {
    if (p === 1 || p === totalPages || (p >= page - delta && p <= page + delta)) {
      pages.push(p);
    } else if (pages[pages.length - 1] !== "…") {
      pages.push("…");
    }
  }

  const start = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const end   = Math.min(page * pageSize, totalItems);

  const btnBase = {
    minWidth:30, height:30, padding:"0 8px", borderRadius:7,
    border:`1px solid ${T.border}`, background:"none", color:T.muted,
    cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:12.5, fontWeight:600,
  };

  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
      padding:"14px 22px", borderTop:`1px solid ${T.border}`, flexWrap:"wrap", gap:10 }}>
      <div style={{ fontSize:12, color:T.muted }}>
        Showing <strong style={{ color:T.text }}>{start}</strong>–<strong style={{ color:T.text }}>{end}</strong> of <strong style={{ color:T.text }}>{totalItems}</strong>
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:5 }}>
        <button disabled={page===1} onClick={() => onChange(page - 1)}
          style={{ ...btnBase, opacity: page===1 ? 0.4 : 1, cursor: page===1 ? "not-allowed" : "pointer" }}>
          ‹
        </button>
        {pages.map((p, i) => p === "…" ? (
          <span key={`gap-${i}`} style={{ color:T.dim, fontSize:12, padding:"0 4px" }}>…</span>
        ) : (
          <button key={p} onClick={() => onChange(p)}
            style={{ ...btnBase,
              background: p===page ? T.cyan : "none",
              borderColor: p===page ? T.cyan : T.border,
              color: p===page ? "#fff" : T.muted,
              fontWeight: p===page ? 800 : 600 }}>
            {p}
          </button>
        ))}
        <button disabled={page===totalPages} onClick={() => onChange(page + 1)}
          style={{ ...btnBase, opacity: page===totalPages ? 0.4 : 1, cursor: page===totalPages ? "not-allowed" : "pointer" }}>
          ›
        </button>
      </div>
    </div>
  );
}

// ── Step indicator ────────────────────────────────────────────────
function StepBar({ step }) {
  const { T } = useTheme();
  const steps = ["Employee Info", "Leave Policy", "Salary Setup"];
  return (
    <div style={{ display:"flex", alignItems:"center", padding:"18px 24px 0", gap:0 }}>
      {steps.map((label, i) => {
        const idx   = i + 1;
        const done  = step > idx;
        const active= step === idx;
        const color = done ? T.green : active ? T.cyan : T.dim;
        return (
          <React.Fragment key={idx}>
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:5, minWidth:100 }}>
              <div style={{
                width:32, height:32, borderRadius:"50%", display:"flex",
                alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:800,
                background: done ? `${T.green}18` : active ? `${T.cyan}18` : T.toggle_bg,
                border: `2px solid ${color}`, color,
              }}>
                {done ? <CheckCircle2 size={16} style={{ color:T.green }}/> : idx}
              </div>
              <span style={{ fontSize:10.5, fontWeight: active ? 700 : 500, color, whiteSpace:"nowrap", letterSpacing:"0.04em" }}>
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div style={{ flex:1, height:2, marginBottom:18, background: step > idx ? T.green : T.toggle_bg, borderRadius:2 }}/>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function EmployeeDirectory() {
  const { T } = useTheme();

  const fInp = (err=false) => ({
    width:"100%", background:T.inp_bg,
    border:`1px solid ${err?T.red:T.border}`,
    borderRadius:8, color:T.text, fontSize:13,
    padding:"9px 12px", outline:"none",
    fontFamily:"Outfit,sans-serif", boxSizing:"border-box",
  });
  const fLbl = {
    fontSize:11.5, color:T.muted, display:"block",
    marginBottom:5, textTransform:"uppercase", letterSpacing:"0.06em",
  };

  const [employees,    setEmployees]    = useState([]);
  const [departments,  setDepartments]  = useState([]);
  const [designations, setDesignations] = useState([]);
  const [leaveTypes,   setLeaveTypes]   = useState([]);
  const [structures,   setStructures]   = useState([]);
  const [stats,        setStats]        = useState({});
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState("");
  const [deptFilter,   setDeptFilter]   = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [branchFilter, setBranchFilter] = useState("");

  // ── Pagination ──────────────────────────────────────────────────
  const PAGE_SIZE = 20;
  const [page, setPage] = useState(1);

  const [open,     setOpen]     = useState(false);
  const [mode,     setMode]     = useState("add");
  const [selected, setSelected] = useState(null);
  const [step,       setStep]       = useState(1);
  const [createdEmp, setCreatedEmp] = useState(null);
  const [selectedLeaves, setSelectedLeaves] = useState([]);
  const [leaveLoading,   setLeaveLoading]   = useState(false);
  const [salaryForm,    setSalaryForm]    = useState({ structure_id:"", ctc:"", effective_from:"" });
  const [salaryLoading, setSalaryLoading] = useState(false);
  const [salaryPreview, setSalaryPreview] = useState(null);
  const [delOpen,     setDelOpen]     = useState(false);
  const [delId,       setDelId]       = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileEmp,  setProfileEmp]  = useState(null);
  const [inviteInfo,  setInviteInfo]  = useState(null);
  const [copied,      setCopied]      = useState(false);
  const [showBulk,    setShowBulk]    = useState(false);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [eRes, dRes, dsRes, sRes, ltRes, strRes] = await Promise.all([
        axios.get(APIConstant.employees,       ax()),
        axios.get(APIConstant.departments,     ax()),
        axios.get(APIConstant.designations,    ax()),
        axios.get(APIConstant.employeeStats,   ax()),
        axios.get(APIConstant.leaveTypes,      ax()),
        axios.get(APIConstant.salaryStructures,ax()),
      ]);
      setEmployees(eRes.data    || []);
      setDepartments(dRes.data  || []);
      setDesignations(dsRes.data|| []);
      setStats(sRes.data        || {});
      setLeaveTypes((ltRes.data || []).filter(lt => lt.is_active));
      setStructures((strRes.data|| []).filter(s  => s.is_active));
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  useEffect(() => {
    const loadPreview = async () => {
      if (!salaryForm.structure_id || !salaryForm.ctc) { setSalaryPreview(null); return; }
      try {
        const r = await axios.get(
          `${APIConstant.salaryStructures}preview?structure_id=${salaryForm.structure_id}&ctc=${salaryForm.ctc}`,
          ax()
        );
        setSalaryPreview(r.data);
      } catch { setSalaryPreview(null); }
    };
    loadPreview();
  }, [salaryForm.structure_id, salaryForm.ctc]);

  // Distinct branch values, derived from whatever employees are already
  // loaded — no extra API call needed since branch_location already
  // lives on the Employee record.
  const branches = useMemo(() => {
    const set = new Set(employees.map(e => e.branch_location).filter(Boolean));
    return Array.from(set).sort();
  }, [employees]);

  const filtered = useMemo(() => employees.filter(e => {
    const s = search.toLowerCase();
    const matchS  = !search || `${e.first_name} ${e.last_name} ${e.email} ${e.employee_code}`.toLowerCase().includes(s);
    const matchD  = !deptFilter   || String(e.department_id) === deptFilter;
    const matchSt = !statusFilter || e.status === statusFilter;
    const matchB  = !branchFilter || e.branch_location === branchFilter;
    return matchS && matchD && matchSt && matchB;
  }), [employees, search, deptFilter, statusFilter, branchFilter]);

  // Reset to page 1 whenever the search term or filters change, so the
  // list doesn't land on a now out-of-range page.
  useEffect(() => { setPage(1); }, [search, deptFilter, statusFilter, branchFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  // Clamp page in case filtered shrinks below the current page (e.g. after a delete)
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [totalPages, page]);

  const paginated = useMemo(() => {
    const startIdx = (page - 1) * PAGE_SIZE;
    return filtered.slice(startIdx, startIdx + PAGE_SIZE);
  }, [filtered, page]);

  // ── Excel download — Employee Directory ──────────────────────────
  // Exports the currently filtered/searched list with all key columns.
  const downloadExcel = () => {
    if (filtered.length === 0) { alert("No employees to export."); return; }

    const rows = filtered.map(e => ({
      "Employee Code":       e.employee_code || "",
      "First Name":          e.first_name || "",
      "Middle Name":         e.middle_name || "",
      "Last Name":           e.last_name || "",
      "Email":               e.email || "",
      "Phone":               e.phone || "",
      "Gender":              e.gender || "",
      "Date of Birth":       e.date_of_birth || "",
      "Marital Status":      e.marital_status || "",
      "Department":          e.department_name || "",
      "Designation":         e.designation_title || "",
      "Designation Level":   e.designation_level || "",
      "Employment Type":     e.employment_type || "",
      "Date of Joining":     e.date_of_joining || "",
      "Branch / Location":   e.branch_location || "",
      "Status":              e.status || "",
      "Reports To":          e.manager_name || e.reporting_supervisor || "",
      "Emergency Contact":   e.emergency_contact || "",
      "Address Line 1":      e.address_line1 || e.address || "",
      "Address Line 2":      e.address_line2 || "",
      "City":                e.city || "",
      "State":               e.state || "",
      "Country":             e.country || "",
      "Pin Code":            e.pin_code || "",
      "PAN Number":          e.pan_number || "",
      "Aadhar Number":       e.aadhar_number || "",
      "UAN Number":          e.uan_number || "",
      "PF Number":           e.pf_number || "",
      "Bank Name":           e.bank_name || "",
      "Bank Account Number": e.bank_account_number || "",
      "Bank IFSC":           e.bank_ifsc || "",
      "Bank Branch":         e.bank_branch || "",
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = Object.keys(rows[0]).map(() => ({ wch: 20 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Employees");
    XLSX.writeFile(wb, `Employee_Directory_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  const openAdd = () => {
    setMode("add"); setSelected(null); setStep(1); setCreatedEmp(null);
    setSelectedLeaves(leaveTypes.map(lt => lt.id));
    setSalaryForm({ structure_id:"", ctc:"", effective_from:"" });
    setSalaryPreview(null); setOpen(true);
  };
  const openEdit = (e) => { setMode("edit"); setSelected(e); setOpen(true); };
  const openView = (e) => { setProfileEmp(e); setProfileOpen(true); };

  const handleClose = () => {
    if (step > 1 && createdEmp) { finishAndShowInvite(); }
    else { setOpen(false); setStep(1); }
  };

  const finishAndShowInvite = () => {
    setOpen(false); setStep(1); fetchAll();
    if (createdEmp?.inviteToken) {
      setInviteInfo({ name: createdEmp.name, email: createdEmp.email, url: createdEmp.inviteUrl });
      setCopied(false);
    }
  };

  const handleSubmit = async (vals) => {
    try {
      const payload = { ...vals,
        department_id:  vals.department_id  ? parseInt(vals.department_id)  : null,
        designation_id: vals.designation_id ? parseInt(vals.designation_id) : null,
        manager_id:     vals.manager_id     ? parseInt(vals.manager_id)     : null,
      };
      if (mode === "edit" && selected) {
        await axios.put(`${APIConstant.employees}${selected.id}`, payload, ax());
        setOpen(false); fetchAll();
      } else {
        const r = await axios.post(APIConstant.employees, payload, ax());
        const empId = r.data?.id;
        const token = r.data?.invite_token;
        setCreatedEmp({
          id: empId, name: `${vals.first_name} ${vals.last_name}`, email: vals.email,
          inviteToken: token,
          inviteUrl:   token ? `${window.location.origin}/setup-account?token=${token}&type=user` : null,
        });
        setStep(2);
      }
    } catch(e) { alert(e.response?.data?.detail || "Error saving employee"); }
  };

  const handleLeaveConfirm = async () => {
    setLeaveLoading(true);
    try { await axios.post(`${APIConstant.leaveTypes}sync-balances`, { employee_id: createdEmp.id }, ax()); }
    catch {}
    finally { setLeaveLoading(false); }
    setStep(3);
  };

  const handleSalaryAssign = async () => {
    if (!salaryForm.structure_id || !salaryForm.ctc) { alert("Please select a structure and enter CTC."); return; }
    setSalaryLoading(true);
    try {
      await axios.post(APIConstant.salaryAssign, {
        employee_id:   parseInt(createdEmp.id),
        structure_id:  parseInt(salaryForm.structure_id),
        ctc:           parseFloat(salaryForm.ctc),
        effective_from: salaryForm.effective_from || new Date().toISOString().split("T")[0],
      }, ax());
      finishAndShowInvite();
    } catch(e) { alert(e.response?.data?.detail || "Error assigning salary."); }
    finally { setSalaryLoading(false); }
  };

  const handleDelete = async () => {
    try { await axios.delete(`${APIConstant.employees}${delId}`, ax()); setDelOpen(false); fetchAll(); }
    catch(e) { console.error(e); }
  };

  const StatusBadge = ({ s }) => {
    const cls = s==="Active"?"green":s==="On Leave"?"amber":"red";
    return <span className={`${styles.badge} ${styles[cls]}`}>{s}</span>;
  };

  const filterSel = {
    background:T.inp_bg, border:`1px solid ${T.border}`, borderRadius:8,
    color:T.text, fontSize:13, padding:"7px 12px", outline:"none", fontFamily:"Outfit,sans-serif",
  };

  return (
    <div className={styles.pageRoot}>

      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.badge}>EMPLOYEE MANAGEMENT</div>
          <h2>Employee Directory</h2>
          <p>Manage all employee profiles, roles and information</p>
        </div>
        <div className={styles.headerRight} style={{ display:"flex", gap:10 }}>
          <button className={styles.btnSecondary} onClick={() => setShowBulk(true)}
            style={{ display:"flex", alignItems:"center", gap:6 }}>
            <Upload size={14}/> Bulk Upload
          </button>
          <button className={styles.btnPrimary} onClick={openAdd}>
            <Plus size={14}/> Add Employee
          </button>
        </div>
      </div>

      <div className={`${styles.statsGrid} ${styles.cols4}`}>
        {[
          { label:"Total Employees", val:stats.total   ||0, color:"cyan"  },
          { label:"Active",          val:stats.active  ||0, color:"green" },
          { label:"On Leave",        val:stats.on_leave||0, color:"amber" },
          { label:"Inactive",        val:stats.inactive||0, color:"red"   },
        ].map(({label,val,color}) => (
          <div key={label} className={`${styles.statCard} ${styles[color]}`}>
            <div className={styles.statVal}>{val}</div>
            <div className={styles.statLabel}>{label}</div>
          </div>
        ))}
      </div>

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h3>All Employees <span style={{fontSize:12,color:T.muted,fontWeight:400}}>({filtered.length})</span></h3>
          <div style={{display:"flex",gap:10}}>
            <button className={styles.btnSecondary} onClick={downloadExcel}
              style={{ display:"flex", alignItems:"center", gap:6 }}>
              <Download size={14}/> Download Excel
            </button>
            <div style={{position:"relative"}}>
              <Search size={13} style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:T.dim}}/>
              <input placeholder="Search…" value={search} onChange={e=>setSearch(e.target.value)}
                style={{...filterSel, padding:"7px 12px 7px 30px", width:200}}/>
            </div>
            <select value={deptFilter} onChange={e=>setDeptFilter(e.target.value)} style={filterSel}>
              <option value="" style={{background:T.opt_bg,color:T.text}}>All Departments</option>
              {departments.map(d=><option key={d.id} value={d.id} style={{background:T.opt_bg,color:T.text}}>{d.name}</option>)}
            </select>
            <select value={branchFilter} onChange={e=>setBranchFilter(e.target.value)} style={filterSel}>
              <option value="" style={{background:T.opt_bg,color:T.text}}>All Branches</option>
              {branches.map(b=><option key={b} value={b} style={{background:T.opt_bg,color:T.text}}>{b}</option>)}
            </select>
            <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} style={filterSel}>
              <option value="" style={{background:T.opt_bg,color:T.text}}>All Status</option>
              <option style={{background:T.opt_bg,color:T.text}}>Active</option>
              <option style={{background:T.opt_bg,color:T.text}}>On Leave</option>
              <option style={{background:T.opt_bg,color:T.text}}>Inactive</option>
            </select>
          </div>
        </div>
        {loading ? (
          <div className={styles.loader}><div className={styles.spinner}/>Loading…</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.darkTable}>
              <thead><tr><th>Employee</th><th>Code</th><th>Department</th><th>Designation</th><th>Contact</th><th>Joined</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {filtered.length===0 ? (
                  <tr className={styles.emptyRow}><td colSpan={8}>No employees found</td></tr>
                ) : paginated.map((e,i) => (
                  <tr key={e.id}>
                    <td>
                      <div style={{display:"flex",alignItems:"center",gap:10}}>
                        <div className={styles.avatar} style={{background:e.avatar_color||COLORS[((page-1)*PAGE_SIZE+i)%COLORS.length]}}>
                          {initials(e.first_name,e.last_name)}
                        </div>
                        <div>
                          <div style={{fontWeight:500,color:T.text}}>{e.first_name} {e.last_name}</div>
                          <div style={{fontSize:11.5,color:T.muted}}>{e.email}</div>
                        </div>
                      </div>
                    </td>
                    <td><span style={{fontFamily:"JetBrains Mono,monospace",fontSize:12,color:T.cyan}}>{e.employee_code||"—"}</span></td>
                    <td style={{color:T.muted}}>{e.department_name||"—"}</td>
                    <td>
                      {e.designation_title ? (
                        <div>
                          <div style={{fontSize:13,color:T.text}}>{e.designation_title}</div>
                          {e.designation_level && <div style={{fontSize:11,color:T.muted}}>{e.designation_level}</div>}
                        </div>
                      ) : <span style={{color:T.dim}}>—</span>}
                    </td>
                    <td>
                      <div style={{fontSize:12,color:T.muted,display:"flex",flexDirection:"column",gap:3}}>
                        {e.phone && <span style={{display:"flex",alignItems:"center",gap:5}}><Phone size={11}/>{e.phone}</span>}
                      </div>
                    </td>
                    <td style={{color:T.muted}}>{e.date_of_joining||"—"}</td>
                    <td><StatusBadge s={e.status}/></td>
                    <td>
                      <div style={{display:"flex",gap:6}}>
                        <button className={styles.btnSm} onClick={()=>openView(e)}><Eye size={12}/>View</button>
                        <button className={styles.btnSm} onClick={()=>openEdit(e)}><Edit2 size={12}/>Edit</button>
                        <button className={styles.btnSm} style={{color:"#FF4D6D",borderColor:"rgba(255,77,109,0.25)"}}
                          onClick={()=>{setDelId(e.id);setDelOpen(true);}}><Trash2 size={12}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && (
          <PaginationBar
            page={page}
            totalPages={totalPages}
            onChange={setPage}
            totalItems={filtered.length}
            pageSize={PAGE_SIZE}
            T={T}
          />
        )}
      </div>

      {showBulk && (
        <BulkEmployeeUpload onClose={()=>setShowBulk(false)} onSuccess={()=>{ setShowBulk(false); fetchAll(); }}/>
      )}

      {/* ════ ADD / EDIT DIALOG ════ */}
      <Dialog open={open} onClose={handleClose} maxWidth={false}
        PaperProps={{ style:{ borderRadius:0, background:"transparent", boxShadow:"none" } }}>
        <style>{`
          .hrms-dlg-form select option { background:${T.opt_bg}; color:${T.text}; }
          .hrms-dlg-form input[type="date"]::-webkit-calendar-picker-indicator { filter:${T.scheme==="light"?"invert(0.5)":"invert(0.7)"}; }
        `}</style>
        <div className="hrms-dlg-form" style={{
          background:T.card, border:`1px solid ${T.cyan}33`,
          borderRadius:14, width: mode==="add" ? 700 : 660,
          overflow:"hidden", fontFamily:"Outfit,sans-serif",
        }}>
          <div style={{padding:"16px 24px",borderBottom:`1px solid ${T.border}`,background:`${T.cyan}08`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <h3 style={{margin:0,fontSize:16,fontWeight:600,color:T.text}}>
                {mode==="edit" ? `Edit — ${selected?.first_name} ${selected?.last_name}` : "Add New Employee"}
              </h3>
              {mode==="add" && <div style={{fontSize:11.5,color:T.muted,marginTop:2}}>Complete all 3 steps to fully set up the employee</div>}
            </div>
            <button style={{background:"none",border:"none",cursor:"pointer",color:T.muted}} onClick={handleClose}><X size={16}/></button>
          </div>
          {mode==="add" && <StepBar step={step}/>}

          <div style={{maxHeight:"72vh",overflowY:"auto"}}>
            {(mode==="edit" || (mode==="add" && step===1)) && (
              <div style={{padding:24}}>
                <Formik
                  enableReinitialize
                  initialValues={mode==="edit" && selected ? {
                    first_name:         selected.first_name          || "",
                    middle_name:        selected.middle_name         || "",
                    last_name:          selected.last_name           || "",
                    email:              selected.email               || "",
                    phone:              selected.phone               || "",
                    date_of_birth:      selected.date_of_birth       || "",
                    gender:             selected.gender              || "",
                    marital_status:     selected.marital_status      || "",
                    department_id:      selected.department_id       || "",
                    designation_id:     selected.designation_id      || "",
                    date_of_joining:    selected.date_of_joining     || "",
                    employment_type:    selected.employment_type     || "Full Time",
                    branch_location:    selected.branch_location     || "",
                    status:             selected.status              || "Active",
                    manager_id:         selected.manager_id          || "",
                    emergency_contact:  selected.emergency_contact   || "",
                    address_line1:      selected.address_line1 || selected.address || "",
                    address_line2:      selected.address_line2       || "",
                    city:               selected.city                || "",
                    state:              selected.state               || "",
                    country:            selected.country             || "India",
                    pin_code:           selected.pin_code            || "",
                    pan_number:         selected.pan_number          || "",
                    aadhar_number:      selected.aadhar_number       || "",
                    uan_number:         selected.uan_number          || "",
                    pf_number:          selected.pf_number           || "",
                    bank_name:          selected.bank_name           || "",
                    bank_account_number:selected.bank_account_number || "",
                    bank_ifsc:          selected.bank_ifsc           || "",
                    bank_branch:        selected.bank_branch         || "",
                  } : EMPTY}
                  validationSchema={Schema}
                  onSubmit={handleSubmit}
                >
                  {({errors,touched})=>(
                    <Form>
                      <div style={{marginBottom:10,fontSize:11,fontWeight:700,color:T.cyan,textTransform:"uppercase",letterSpacing:"0.08em",paddingBottom:6,borderBottom:`1px solid ${T.border}`}}>👤 Basic Information</div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:14,marginBottom:14}}>
                        {[
                          {name:"first_name",   label:"First Name *",  type:"text"},
                          {name:"middle_name",  label:"Middle Name",   type:"text"},
                          {name:"last_name",    label:"Last Name *",   type:"text"},
                          {name:"email",        label:"Email *",       type:"email"},
                          {name:"phone",        label:"Phone",         type:"text"},
                          {name:"date_of_birth",label:"Date of Birth", type:"date"},
                        ].map(({name,label,type})=>(
                          <div key={name}>
                            <label style={fLbl}>{label}</label>
                            <Field name={name} type={type} placeholder={label.replace(" *","")} style={fInp(errors[name]&&touched[name])}/>
                            {errors[name]&&touched[name]&&<p style={{color:T.red,fontSize:11.5,marginTop:4}}>{errors[name]}</p>}
                          </div>
                        ))}
                        <div>
                          <label style={fLbl}>Gender</label>
                          <Field as="select" name="gender" style={fInp()}>
                            <option value="">Select</option>
                            <option>Male</option><option>Female</option><option>Other</option>
                          </Field>
                        </div>
                        <div>
                          <label style={fLbl}>Marital Status</label>
                          <Field as="select" name="marital_status" style={fInp()}>
                            <option value="">Select</option>
                            <option>Single</option><option>Married</option><option>Divorced</option><option>Widowed</option>
                          </Field>
                        </div>
                        <div>
                          <label style={fLbl}>Emergency Contact</label>
                          <Field name="emergency_contact" type="text" placeholder="Name & phone" style={fInp()}/>
                        </div>
                      </div>

                      <div style={{marginBottom:10,fontSize:11,fontWeight:700,color:T.purple,textTransform:"uppercase",letterSpacing:"0.08em",paddingBottom:6,borderBottom:`1px solid ${T.border}`}}>🏢 Role & Organisation</div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
                        <div>
                          <label style={fLbl}>Department</label>
                          <Field as="select" name="department_id" style={fInp()}>
                            <option value="">Select Department</option>
                            {departments.map(d=><option key={d.id} value={d.id}>{d.name}</option>)}
                          </Field>
                        </div>
                        <div>
                          <label style={fLbl}>Designation</label>
                          <Field as="select" name="designation_id" style={fInp()}>
                            <option value="">Select Designation</option>
                            {designations.map(d=><option key={d.id} value={d.id}>{d.title}</option>)}
                          </Field>
                        </div>
                        <div><label style={fLbl}>Date of Joining</label><Field name="date_of_joining" type="date" style={fInp()}/></div>
                        <div>
                          <label style={fLbl}>Employment Type</label>
                          <Field as="select" name="employment_type" style={fInp()}>
                            <option>Full Time</option><option>Part Time</option><option>Contract</option><option>Intern</option>
                          </Field>
                        </div>
                        <div>
                          <label style={fLbl}>Status</label>
                          <Field as="select" name="status" style={fInp()}>
                            <option>Active</option><option>On Leave</option><option>Inactive</option>
                          </Field>
                        </div>
                        <div><label style={fLbl}>Branch / Location</label><Field name="branch_location" type="text" placeholder="e.g. Bangalore HQ" style={fInp()}/></div>
                      </div>
                      <div style={{marginBottom:14}}>
                        <label style={fLbl}>Reports To (Manager)</label>
                        <Field as="select" name="manager_id" style={fInp()}>
                          <option value="">— No manager (top level) —</option>
                          {employees.filter(e=>mode==="add"||e.id!==selected?.id).map(e=>(
                            <option key={e.id} value={e.id}>{e.first_name} {e.last_name}{e.designation_title?` — ${e.designation_title}`:""}</option>
                          ))}
                        </Field>
                      </div>

                      <div style={{marginBottom:10,fontSize:11,fontWeight:700,color:T.green,textTransform:"uppercase",letterSpacing:"0.08em",paddingBottom:6,borderBottom:`1px solid ${T.border}`}}>📍 Address</div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
                        <div><label style={fLbl}>Address Line 1</label><Field name="address_line1" type="text" placeholder="Street / flat" style={fInp()}/></div>
                        <div><label style={fLbl}>Address Line 2</label><Field name="address_line2" type="text" placeholder="Area / locality" style={fInp()}/></div>
                        <div><label style={fLbl}>City</label><Field name="city" type="text" placeholder="City" style={fInp()}/></div>
                        <div><label style={fLbl}>State</label><Field name="state" type="text" placeholder="State" style={fInp()}/></div>
                        <div><label style={fLbl}>Country</label><Field name="country" type="text" placeholder="Country" style={fInp()}/></div>
                        <div><label style={fLbl}>Pin Code</label><Field name="pin_code" type="text" placeholder="Pin code" style={fInp()}/></div>
                      </div>

                      <div style={{marginBottom:10,fontSize:11,fontWeight:700,color:T.amber,textTransform:"uppercase",letterSpacing:"0.08em",paddingBottom:6,borderBottom:`1px solid ${T.border}`}}>🏛️ PF & Tax Details</div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
                        {[
                          {name:"pan_number",    label:"PAN Number",   placeholder:"ABCDE1234F"},
                          {name:"aadhar_number", label:"Aadhar Number",placeholder:"XXXX XXXX XXXX"},
                          {name:"uan_number",    label:"UAN Number",   placeholder:"100XXXXXXXXX"},
                          {name:"pf_number",     label:"PF Member ID", placeholder:"MH/BAN/..."},
                        ].map(({name,label,placeholder})=>(
                          <div key={name}>
                            <label style={fLbl}>{label}</label>
                            <Field name={name} placeholder={placeholder} style={{...fInp(),fontFamily:"monospace"}}/>
                          </div>
                        ))}
                      </div>

                      <div style={{marginBottom:10,fontSize:11,fontWeight:700,color:T.green,textTransform:"uppercase",letterSpacing:"0.08em",paddingBottom:6,borderBottom:`1px solid ${T.border}`}}>🏦 Bank Account Details</div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:20}}>
                        {[
                          {name:"bank_name",           label:"Bank Name",      placeholder:"e.g. State Bank of India"},
                          {name:"bank_account_number", label:"Account Number", placeholder:"Account number"},
                          {name:"bank_ifsc",           label:"IFSC Code",      placeholder:"e.g. SBIN0001234"},
                          {name:"bank_branch",         label:"Branch Name",    placeholder:"e.g. Koramangala"},
                        ].map(({name,label,placeholder})=>(
                          <div key={name}>
                            <label style={fLbl}>{label}</label>
                            <Field name={name} placeholder={placeholder} style={fInp()}/>
                          </div>
                        ))}
                      </div>

                      <div style={{display:"flex",gap:10,justifyContent:"flex-end",paddingTop:12,borderTop:`1px solid ${T.border}`}}>
                        <button type="button" onClick={handleClose} className={styles.btnSecondary}>Cancel</button>
                        <button type="submit" className={styles.btnPrimary}>
                          {mode==="edit" ? "Update Employee" : <>Next: Leave Policy <ChevronRight size={14}/></>}
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            )}

            {/* ════ STEP 2 — LEAVE POLICY ════ */}
            {mode==="add" && step===2 && (
              <div style={{padding:24}}>
                <div style={{background:`${T.green}0A`,border:`1px solid ${T.green}26`,borderRadius:10,padding:"12px 16px",marginBottom:20,display:"flex",alignItems:"center",gap:12}}>
                  <CheckCircle2 size={20} style={{color:T.green,flexShrink:0}}/>
                  <div>
                    <div style={{fontSize:13,fontWeight:700,color:T.green}}>Employee created successfully!</div>
                    <div style={{fontSize:12,color:T.muted,marginTop:2}}><strong style={{color:T.text}}>{createdEmp?.name}</strong> · {createdEmp?.email}</div>
                  </div>
                </div>
                <div style={{fontSize:11,fontWeight:700,color:T.cyan,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:14,paddingBottom:8,borderBottom:`1px solid ${T.border}`}}>📋 Leave Policy Assignment</div>
                <p style={{fontSize:13,color:T.muted,marginBottom:16,lineHeight:1.7}}>
                  The following active leave types will be assigned to <strong style={{color:T.text}}>{createdEmp?.name}</strong>.
                </p>
                {leaveTypes.length === 0 ? (
                  <div style={{padding:"24px",textAlign:"center",background:T.toggle_bg,border:`1px solid ${T.border}`,borderRadius:10,color:T.muted,fontSize:13}}>
                    No active leave types configured yet.
                  </div>
                ) : (
                  <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:20}}>
                    {leaveTypes.map(lt => {
                      const checked = selectedLeaves.includes(lt.id);
                      return (
                        <div key={lt.id}
                          onClick={() => setSelectedLeaves(prev => prev.includes(lt.id) ? prev.filter(id=>id!==lt.id) : [...prev, lt.id])}
                          style={{ display:"flex", alignItems:"center", gap:14, padding:"12px 16px", borderRadius:10, cursor:"pointer",
                            background: checked ? `${T.cyan}0A` : T.toggle_bg,
                            border:`1px solid ${checked ? T.cyan+"33" : T.border}`, transition:"all 0.2s" }}>
                          <div style={{width:10,height:10,borderRadius:"50%",background:lt.color,flexShrink:0}}/>
                          <div style={{flex:1}}>
                            <div style={{fontSize:13,fontWeight:600,color:T.text}}>{lt.name}</div>
                            <div style={{fontSize:11.5,color:T.muted,marginTop:2}}>
                              {lt.default_days===0?"Unlimited":lt.default_days+" days/year"}
                              {lt.carry_forward && " · Carry forward"}
                              {!lt.is_paid && " · Unpaid/LOP"}
                            </div>
                          </div>
                          <div style={{ width:20,height:20,borderRadius:5,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",
                            background:checked?T.cyan:"transparent", border:`2px solid ${checked?T.cyan:T.border}` }}>
                            {checked && <Check size={12} style={{color:"#fff"}}/>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div style={{display:"flex",gap:10,justifyContent:"space-between",paddingTop:12,borderTop:`1px solid ${T.border}`}}>
                  <button onClick={finishAndShowInvite} style={{padding:"9px 16px",borderRadius:8,border:`1px solid ${T.border}`,background:"none",color:T.muted,cursor:"pointer",fontFamily:"Outfit,sans-serif",fontSize:13}}>Skip for now</button>
                  <button onClick={handleLeaveConfirm} disabled={leaveLoading} className={styles.btnPrimary}>
                    {leaveLoading ? "Confirming…" : <>Confirm & Next: Salary <ChevronRight size={14}/></>}
                  </button>
                </div>
              </div>
            )}

            {/* ════ STEP 3 — SALARY SETUP ════ */}
            {mode==="add" && step===3 && (
              <div style={{padding:24}}>
                <div style={{fontSize:11,fontWeight:700,color:T.cyan,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:14,paddingBottom:8,borderBottom:`1px solid ${T.border}`}}>💰 Salary Structure Assignment</div>
                <p style={{fontSize:13,color:T.muted,marginBottom:18,lineHeight:1.7}}>
                  Assign a salary structure and CTC to <strong style={{color:T.text}}>{createdEmp?.name}</strong>.
                </p>
                <div style={{display:"grid",gridTemplateColumns: salaryPreview ? "1fr 1fr" : "1fr",gap:20}}>
                  <div style={{display:"flex",flexDirection:"column",gap:14}}>
                    <div>
                      <label style={fLbl}>Salary Structure *</label>
                      {structures.length === 0 ? (
                        <div style={{padding:"12px 14px",background:`${T.amber}0A`,border:`1px solid ${T.amber}26`,borderRadius:8,fontSize:12.5,color:T.amber}}>
                          ⚠️ No active salary structures. Create one from Payroll → Salary Structures first.
                        </div>
                      ) : (
                        <select value={salaryForm.structure_id} onChange={e=>setSalaryForm({...salaryForm,structure_id:e.target.value})} style={fInp(!salaryForm.structure_id)}>
                          <option value="">Select structure…</option>
                          {structures.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                      )}
                    </div>
                    <div>
                      <label style={fLbl}>Annual CTC (₹) *</label>
                      <input type="number" min={0} value={salaryForm.ctc} onChange={e=>setSalaryForm({...salaryForm,ctc:e.target.value})} placeholder="e.g. 1200000" style={fInp(!salaryForm.ctc)}/>
                      {salaryForm.ctc && <div style={{fontSize:12,color:T.cyan,marginTop:5}}>Monthly CTC: {fmt(salaryForm.ctc/12)}</div>}
                    </div>
                    <div>
                      <label style={fLbl}>Effective From</label>
                      <input type="date" value={salaryForm.effective_from} onChange={e=>setSalaryForm({...salaryForm,effective_from:e.target.value})} style={fInp()}/>
                    </div>
                    {salaryPreview && (
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                        {[
                          {label:"Gross",        val:salaryPreview.gross,           color:T.amber},
                          {label:"Net Take Home",val:salaryPreview.net_salary,      color:T.green},
                          {label:"Monthly TDS",  val:salaryPreview.tds_monthly,     color:T.red  },
                          {label:"CTC",          val:salaryPreview.cost_to_company, color:T.cyan },
                        ].map(({label,val,color})=>(
                          <div key={label} style={{background:T.toggle_bg,border:`1px solid ${T.border}`,borderRadius:8,padding:"10px 12px"}}>
                            <div style={{fontSize:10.5,color:T.dim,marginBottom:3,textTransform:"uppercase",letterSpacing:"0.06em"}}>{label}</div>
                            <div style={{fontSize:14,fontWeight:800,fontFamily:"JetBrains Mono,monospace",color}}>{fmt(val)}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {salaryPreview && (
                    <div style={{background:`${T.cyan}06`,border:`1px solid ${T.border}`,borderRadius:10,overflow:"hidden"}}>
                      <div style={{padding:"10px 14px",borderBottom:`1px solid ${T.border}`,fontSize:11,fontWeight:700,color:T.cyan,textTransform:"uppercase",letterSpacing:"0.08em"}}>Monthly Breakdown</div>
                      {Object.entries(salaryPreview.earnings||{}).map(([k,v])=>(
                        <div key={k} style={{display:"flex",justifyContent:"space-between",padding:"7px 14px",borderBottom:`1px solid ${T.toggle_bg}`}}>
                          <span style={{fontSize:12.5,color:T.muted}}>{k}</span>
                          <span style={{fontSize:12.5,fontFamily:"JetBrains Mono,monospace",color:T.text}}>{fmt(v)}</span>
                        </div>
                      ))}
                      <div style={{display:"flex",justifyContent:"space-between",padding:"8px 14px",background:`${T.amber}0C`,borderBottom:`1px solid ${T.border}`}}>
                        <span style={{fontSize:13,fontWeight:700,color:T.amber}}>Gross</span>
                        <span style={{fontSize:13,fontWeight:800,fontFamily:"JetBrains Mono,monospace",color:T.amber}}>{fmt(salaryPreview.gross)}</span>
                      </div>
                      {Object.entries(salaryPreview.deductions||{}).filter(([k])=>k!=="TDS").map(([k,v])=>(
                        <div key={k} style={{display:"flex",justifyContent:"space-between",padding:"7px 14px",borderBottom:`1px solid ${T.toggle_bg}`}}>
                          <span style={{fontSize:12.5,color:T.muted}}>− {k}</span>
                          <span style={{fontSize:12.5,fontFamily:"JetBrains Mono,monospace",color:T.red}}>{fmt(v)}</span>
                        </div>
                      ))}
                      {salaryPreview.tds_monthly>0 && (
                        <div style={{display:"flex",justifyContent:"space-between",padding:"7px 14px",borderBottom:`1px solid ${T.toggle_bg}`}}>
                          <span style={{fontSize:12.5,color:T.muted}}>− TDS</span>
                          <span style={{fontSize:12.5,fontFamily:"JetBrains Mono,monospace",color:T.red}}>{fmt(salaryPreview.tds_monthly)}</span>
                        </div>
                      )}
                      <div style={{display:"flex",justifyContent:"space-between",padding:"10px 14px",background:`${T.green}0C`,borderTop:`1px solid ${T.border}`}}>
                        <span style={{fontSize:13,fontWeight:700,color:T.green}}>Net Salary</span>
                        <span style={{fontSize:14,fontWeight:900,fontFamily:"JetBrains Mono,monospace",color:T.green}}>{fmt(salaryPreview.net_salary)}</span>
                      </div>
                    </div>
                  )}
                </div>
                <div style={{display:"flex",gap:10,justifyContent:"space-between",paddingTop:16,marginTop:6,borderTop:`1px solid ${T.border}`}}>
                  <button onClick={finishAndShowInvite} style={{padding:"9px 16px",borderRadius:8,border:`1px solid ${T.border}`,background:"none",color:T.muted,cursor:"pointer",fontFamily:"Outfit,sans-serif",fontSize:13}}>Skip for now</button>
                  <div style={{display:"flex",gap:8}}>
                    <button onClick={()=>setStep(2)} className={styles.btnSecondary}>← Back</button>
                    <button onClick={handleSalaryAssign} disabled={salaryLoading||!salaryForm.structure_id||!salaryForm.ctc}
                      className={styles.btnPrimary} style={{opacity:(!salaryForm.structure_id||!salaryForm.ctc)?0.5:1}}>
                      {salaryLoading ? "Assigning…" : <><DollarSign size={14}/> Finish Setup</>}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Dialog>

      {/* ════ FULL PROFILE MODAL ════ */}
      <FullProfileModal
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        emp={profileEmp}
        T={T}
        departments={departments}
        designations={designations}
        employees={employees}
        onRefresh={fetchAll}
      />

      <Popup open={delOpen} message="Delete this employee? This cannot be undone."
        handleClose={()=>setDelOpen(false)} handleConfirm={handleDelete}/>

      {/* Invite Link Modal */}
      <Dialog open={!!inviteInfo} onClose={()=>setInviteInfo(null)}
        PaperProps={{ style:{ borderRadius:0, background:"transparent", boxShadow:"none" } }}>
        <div style={{background:T.card,border:`1px solid ${T.green}40`,borderRadius:16,width:480,padding:32,fontFamily:"Outfit,sans-serif"}}>
          <div style={{textAlign:"center",marginBottom:24}}>
            <div style={{fontSize:52,marginBottom:10}}>🎉</div>
            <h3 style={{fontSize:20,fontWeight:800,color:T.green,margin:"0 0 6px"}}>Employee Setup Complete!</h3>
            <p style={{fontSize:13,color:T.muted,margin:0}}>
              <strong style={{color:T.text}}>{inviteInfo?.name}</strong> is fully set up — leave policy and salary structure assigned.
            </p>
          </div>
          <div style={{background:`${T.cyan}0A`,border:`1px solid ${T.border}`,borderRadius:10,padding:"14px 18px",marginBottom:18}}>
            <div style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>Account Setup Link</div>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:8,fontSize:13}}>
              <span style={{color:T.muted}}>Email</span>
              <span style={{color:T.text,fontWeight:500}}>{inviteInfo?.email}</span>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:12,fontSize:13}}>
              <span style={{color:T.muted}}>Status</span>
              <span style={{fontSize:11.5,padding:"2px 10px",borderRadius:10,background:`${T.amber}1A`,color:T.amber,fontWeight:600}}>📧 Invite Email Sent</span>
            </div>
            <div style={{fontSize:11,color:T.muted,marginBottom:6}}>Setup URL (share if email fails):</div>
            <div style={{fontSize:11.5,fontFamily:"JetBrains Mono,monospace",color:T.cyan,background:T.toggle_bg,borderRadius:7,padding:"9px 12px",wordBreak:"break-all",lineHeight:1.6,marginBottom:10}}>
              {inviteInfo?.url || "— No invite token (may already have an account) —"}
            </div>
            {inviteInfo?.url && (
              <button
                onClick={()=>{ navigator.clipboard.writeText(inviteInfo.url).catch(()=>{}); setCopied(true); setTimeout(()=>setCopied(false),2500); }}
                style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:12,padding:"7px 14px",borderRadius:7,
                  border:`1px solid ${copied ? T.green+"4D" : T.border}`,
                  background:copied ? `${T.green}10` : `${T.cyan}0A`,
                  color:copied ? T.green : T.cyan,
                  cursor:"pointer",fontFamily:"Outfit,sans-serif",fontWeight:600}}>
                {copied ? <><Check size={13}/>Copied!</> : <><Copy size={13}/>Copy Setup Link</>}
              </button>
            )}
          </div>
          <div style={{background:`${T.purple}0A`,border:`1px solid ${T.border}`,borderRadius:8,padding:"10px 14px",fontSize:12.5,color:T.muted,marginBottom:20}}>
            💡 The employee will use this link to set their password and activate their account. Link expires in <strong style={{color:T.text}}>72 hours</strong>.
          </div>
          <button onClick={()=>setInviteInfo(null)}
            style={{width:"100%",background:`linear-gradient(135deg,${T.cyan},${T.purple})`,border:"none",borderRadius:9,color:"#fff",fontFamily:"Outfit,sans-serif",fontSize:13,fontWeight:700,padding:12,cursor:"pointer"}}>
            Done ✓
          </button>
        </div>
      </Dialog>
    </div>
  );
}